<script setup lang="ts">
/*
 * Pay-module sub-nav. Mounts in the TopBar `subnav` slot whenever the
 * active route is under `/pay/*`. Highlights the active tab based on
 * the current route name; nested routes (e.g. `pay-run-detail` under
 * Payroll Center) keep the right parent tab active via
 * `alsoActiveFor`.
 *
 * Markup matches the V6 proto's `.sub-nav` block (lines ~4478–4484 of
 * `redesign/optimal-hr-prototype-v6.html`): a flat `<nav class="sub-nav">`
 * with `<a>` children that flip the `active` class. Styling lives in
 * `./shell-styles.css`.
 */
import { computed } from 'vue';
import { RouterLink, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';

interface Tab {
  routeName: string;
  labelKey: string;
  /**
   * Additional route names that should keep this tab highlighted.
   * Lets nested routes (e.g. `pay-run-detail` under Payroll Center)
   * still show the right active tab.
   */
  alsoActiveFor?: string[];
}

const TABS: Tab[] = [
  {
    routeName: 'pay',
    labelKey: 'v2.shell.pay-subnav.center',
    alsoActiveFor: ['pay-run', 'pay-run-detail'],
  },
  { routeName: 'pay-input-center', labelKey: 'v2.shell.pay-subnav.input' },
  { routeName: 'pay-reports', labelKey: 'v2.shell.pay-subnav.reports' },
  { routeName: 'pay-templates', labelKey: 'v2.shell.pay-subnav.templates' },
  { routeName: 'pay-configurations', labelKey: 'v2.shell.pay-subnav.configurations' },
];

const route = useRoute();
const { t } = useI18n();

function isActive(tab: Tab): boolean {
  const name = route.name?.toString();
  if (!name) return false;
  if (name === tab.routeName) return true;
  return tab.alsoActiveFor?.includes(name) ?? false;
}

const activeTab = computed(() => TABS.find(isActive)?.routeName ?? null);
</script>

<template>
  <!--
    The outer wrapper carries Tailwind utilities for the page-content
    max-width + horizontal padding (matching the top-nav row), and the
    inner <nav class="sub-nav"> picks up the proto's tab styling.
  -->
  <div class="app-container">
    <nav class="sub-nav" aria-label="Pay sub-navigation" data-testid="pay-subnav">
      <RouterLink
        v-for="tab in TABS"
        :key="tab.routeName"
        :to="{ name: tab.routeName }"
        :class="{ active: activeTab === tab.routeName }"
        :aria-current="activeTab === tab.routeName ? 'page' : undefined"
        :data-testid="`pay-subnav-${tab.routeName}`"
      >
        {{ t(tab.labelKey) }}
      </RouterLink>
    </nav>
  </div>
</template>
